import { CONFIDENCE_ORDER, secretConfidenceSchema, } from '../contracts/shared/secret-confidence';
// Re-export the shared type/schema/order so consumers only need one import path
// for everything confidence-related (mirrors how `helpers/severity` sits next to
// its `contracts/shared/severity` schema).
export { CONFIDENCE_ORDER, secretConfidenceSchema };
/**
 * Entropy band boundaries. Anchored to Gitleaks' own default rule thresholds
 * (config/gitleaks.toml): ~78% of entropy rules require <= 3.0, and 4.5 is the
 * HIGHEST threshold any default rule uses — so `>= 4.5` clears every Gitleaks
 * rule and `>= 3.0` clears its stricter ones. Not an arbitrary split.
 *
 * SINGLE SOURCE OF TRUTH: these bands live only here. The backend resolves a
 * confidence level to a numeric cutoff via {@link minEntropyForConfidence} and
 * ships that number to the Go agent / IDE extension, which compare numerically
 * and never re-encode the bands.
 */
export const CONFIDENCE_MEDIUM_MIN = 3.0;
export const CONFIDENCE_HIGH_MIN = 4.5;
/**
 * Map a Gitleaks entropy score to a confidence level.
 *
 * @param entropy - Shannon entropy (bits/char) reported by Gitleaks.
 * @returns `'high'` at/above {@link CONFIDENCE_HIGH_MIN}, `'medium'` at/above
 *   {@link CONFIDENCE_MEDIUM_MIN}, else `'low'`.
 */
export function getEntropyConfidence(entropy) {
    if (entropy >= CONFIDENCE_HIGH_MIN)
        return 'high';
    if (entropy >= CONFIDENCE_MEDIUM_MIN)
        return 'medium';
    return 'low';
}
/**
 * The minimum entropy a finding must have to clear a given confidence level.
 * This is the numeric cutoff the backend ships to clients.
 *
 * @param level - the confidence threshold.
 * @returns `null` for `'low'` (no cutoff — everything passes), `3.0` for
 *   `'medium'`, `4.5` for `'high'`.
 */
export function minEntropyForConfidence(level) {
    switch (level) {
        case 'high':
            return CONFIDENCE_HIGH_MIN;
        case 'medium':
            return CONFIDENCE_MEDIUM_MIN;
        case 'low':
            return null;
    }
}
/**
 * True when a finding's entropy clears the confidence threshold.
 *
 * Findings with no entropy (regex/file-pattern secrets, older clients) ALWAYS
 * pass — confidence only ever suppresses Gitleaks findings whose entropy is
 * below the threshold.
 *
 * @param entropy - the finding's Shannon entropy, or `undefined` when absent.
 * @param level - the configured confidence threshold.
 */
export function meetsConfidenceThreshold(entropy, level) {
    const min = minEntropyForConfidence(level);
    if (min === null) {
        return true;
    }
    if (entropy === undefined) {
        return true;
    }
    return entropy >= min;
}
/**
 * True when a finding clears an already-resolved numeric entropy cutoff.
 *
 * This is the comparison every non-frontend consumer uses once the backend has
 * resolved the confidence level to a numeric `minEntropy` (notify decision,
 * outcome derivation, Go agent, IDE extension). It is the exact pure predicate
 * the plan pins for BE↔client parity: `cutoff absent OR entropy absent OR
 * entropy >= cutoff`. Keeping it here (not re-deriving bands per consumer)
 * guarantees the two clients and the backend can never drift.
 *
 * @param entropy - the finding's Shannon entropy, or `null`/`undefined` when
 *   absent (regex/file-pattern secrets, older clients) — always passes.
 * @param minEntropy - the resolved cutoff, or `null`/`undefined` for "no cutoff"
 *   (flag off, or threshold `low`) — everything passes.
 */
export function meetsEntropyCutoff(entropy, minEntropy) {
    if (minEntropy === null || minEntropy === undefined) {
        return true;
    }
    if (entropy === null || entropy === undefined) {
        return true;
    }
    return entropy >= minEntropy;
}
//# sourceMappingURL=secret-confidence.js.map